import { useState, useRef } from 'react'
import { Upload, Download, FileSpreadsheet, AlertCircle, X } from 'lucide-react'
import { api } from '../lib/api'
import { trackEvent } from '../lib/analytics'

// Generic CSV import/export controls — used across all admin tables that
// support the bulk workflow (products, categories, discount codes,
// promotions, addons). The page passes:
//   basePath         — the admin route prefix, e.g. '/products'
//   templateFilename — what the downloaded template is named
//   exportFilename   — what the export download is named
//   onImported       — callback so the page can invalidate queries after
//                      a successful commit
//
// Endpoint contract (each basePath must expose these four routes):
//   GET    {basePath}/csv/template  → CSV body
//   GET    {basePath}/csv/export    → CSV body
//   POST   {basePath}/csv/preview   ← CSV body, returns CsvPreview JSON
//   POST   {basePath}/csv/import    ← CSV body, returns { created, updated }
//
// Two-stage import (file → server dry-run → preview modal → confirm →
// commit) — the commit endpoint rejects writes when any error is present
// in the preview so the admin can't half-apply a broken CSV.

interface CsvPreview {
  would_create: number
  would_update: number
  error_count: number
  errors: Array<{ row: number; field?: string; message: string }>
  sample_create: Array<{ row: number; name: string | null; sku?: string | null; code?: string | null }>
  sample_update: Array<{ row: number; name: string | null; sku?: string | null; code?: string | null }>
}

interface Props {
  basePath: string
  templateFilename?: string  // optional in exportOnly mode
  exportFilename: string
  onImported?: () => void    // optional in exportOnly mode
  label?: string  // singular noun shown in errors / toasts (default "row")
  // When true, hides Template + Import buttons. For read-only tables
  // (orders, customers, drivers, AI logs) where bulk import would be
  // unsafe — we only offer the export download.
  exportOnly?: boolean
  // Append-able query string for the export URL — e.g. `?period=7d` so
  // AI Logs export honors the period filter currently in view.
  exportQuery?: string
}

export default function CsvControls({
  basePath,
  templateFilename,
  exportFilename,
  onImported,
  label = 'row',
  exportOnly = false,
  exportQuery = '',
}: Props) {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [pendingCsv, setPendingCsv] = useState<{ filename: string; body: string; preview: CsvPreview } | null>(null)
  const [busy, setBusy] = useState<'preview' | 'commit' | null>(null)
  const [toast, setToast] = useState<string | null>(null)

  async function downloadCsv(suffix: 'csv/template' | 'csv/export', filename: string) {
    try {
      const fetchUrl = suffix === 'csv/export' && exportQuery
        ? `${basePath}/${suffix}${exportQuery}`
        : `${basePath}/${suffix}`
      const res = await api.get(fetchUrl, { responseType: 'blob' })
      const blobUrl = URL.createObjectURL(res.data)
      const a = document.createElement('a')
      a.href = blobUrl
      a.download = filename
      document.body.appendChild(a)
      a.click()
      a.remove()
      URL.revokeObjectURL(blobUrl)
      // GA4: only `csv/export` is a user-meaningful event (template
      // downloads are a setup step). Carry `basePath` as `surface` so
      // we can break exports down per admin page (orders, customers,
      // shifts, etc.) without instrumenting each call site.
      if (suffix === 'csv/export') {
        trackEvent('admin_csv_export', {
          surface: basePath,
          has_filters: exportQuery.length > 0,
        })
      }
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { message?: string } }; message?: string })?.response?.data?.message
        ?? (e as { message?: string })?.message
        ?? 'Download failed'
      setToast(msg)
    }
  }

  async function onFileChosen(file: File) {
    setBusy('preview')
    try {
      const body = await file.text()
      const res = await api.post(`${basePath}/csv/preview`, body, {
        headers: { 'content-type': 'text/csv' },
      })
      setPendingCsv({ filename: file.name, body, preview: res.data as CsvPreview })
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'Preview failed'
      setToast(msg)
    } finally {
      setBusy(null)
      if (fileInputRef.current) fileInputRef.current.value = ''
    }
  }

  async function commitImport() {
    if (!pendingCsv) return
    setBusy('commit')
    try {
      const res = await api.post(`${basePath}/csv/import`, pendingCsv.body, {
        headers: { 'content-type': 'text/csv' },
      })
      const { created, updated } = res.data as { created: number; updated: number }
      setToast(`Imported — ${created} created, ${updated} updated`)
      setPendingCsv(null)
      onImported?.()
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'Import failed'
      setToast(msg)
    } finally {
      setBusy(null)
    }
  }

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        accept=".csv,text/csv"
        className="hidden"
        onChange={e => {
          const f = e.target.files?.[0]
          if (f) onFileChosen(f)
        }}
      />
      {!exportOnly && templateFilename && (
        <button
          onClick={() => downloadCsv('csv/template', templateFilename)}
          title="Download a CSV template with one example row"
          className="flex items-center gap-1.5 bg-[#1a1a1a] border border-[#2e2e2e] hover:border-[#3a3a3a] text-gray-300 text-xs px-3 py-2 rounded-lg transition-colors"
        >
          <FileSpreadsheet size={14} /> Template
        </button>
      )}
      <button
        onClick={() => downloadCsv('csv/export', exportFilename)}
        title="Export current data as CSV"
        className="flex items-center gap-1.5 bg-[#1a1a1a] border border-[#2e2e2e] hover:border-[#3a3a3a] text-gray-300 text-xs px-3 py-2 rounded-lg transition-colors"
      >
        <Download size={14} /> Export
      </button>
      {!exportOnly && (
        <button
          onClick={() => fileInputRef.current?.click()}
          disabled={busy !== null}
          title="Upload a CSV to bulk-create / bulk-update"
          className="flex items-center gap-1.5 bg-[#1a1a1a] border border-[#2e2e2e] hover:border-[#3a3a3a] text-gray-300 text-xs px-3 py-2 rounded-lg transition-colors disabled:opacity-50"
        >
          <Upload size={14} /> {busy === 'preview' ? 'Validating…' : 'Import'}
        </button>
      )}

      {pendingCsv && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
          <div className="bg-[#1a1a1a] border border-[#2e2e2e] rounded-xl max-w-2xl w-full max-h-[85vh] flex flex-col">
            <div className="px-5 py-4 border-b border-[#2e2e2e] flex items-center justify-between">
              <div>
                <h3 className="text-white font-semibold">Import preview</h3>
                <p className="text-xs text-gray-500 mt-0.5">{pendingCsv.filename}</p>
              </div>
              <button onClick={() => setPendingCsv(null)} className="text-gray-500 hover:text-white">
                <X size={18} />
              </button>
            </div>
            <div className="px-5 py-4 overflow-y-auto">
              <div className="grid grid-cols-3 gap-3 mb-4">
                <PreviewCount label="Will create" value={pendingCsv.preview.would_create} color="text-emerald-400" />
                <PreviewCount label="Will update" value={pendingCsv.preview.would_update} color="text-blue-400" />
                <PreviewCount label="Errors" value={pendingCsv.preview.error_count} color={pendingCsv.preview.error_count > 0 ? 'text-[#C41E3A]' : 'text-gray-500'} />
              </div>
              {pendingCsv.preview.sample_create.length > 0 && (
                <SampleList title="Sample creates" rows={pendingCsv.preview.sample_create} />
              )}
              {pendingCsv.preview.sample_update.length > 0 && (
                <SampleList title="Sample updates" rows={pendingCsv.preview.sample_update} />
              )}
              {pendingCsv.preview.errors.length > 0 && (
                <div>
                  <p className="text-xs uppercase tracking-wide text-[#C41E3A] font-semibold mb-1.5 flex items-center gap-1">
                    <AlertCircle size={12} /> Errors (showing first {pendingCsv.preview.errors.length} of {pendingCsv.preview.error_count})
                  </p>
                  <ul className="text-xs text-gray-300 space-y-1 bg-[#0f0f0f] border border-[#2e2e2e] rounded p-3 max-h-60 overflow-y-auto font-mono">
                    {pendingCsv.preview.errors.map((e, i) => (
                      <li key={i}>
                        <span className="text-gray-600">row {e.row}{e.field ? ` · ${e.field}` : ''}:</span> {e.message}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
            <div className="px-5 py-4 border-t border-[#2e2e2e] flex items-center justify-between">
              <p className="text-xs text-gray-500">
                {pendingCsv.preview.error_count > 0
                  ? `Fix errors in the CSV before importing.`
                  : `Click Apply to commit ${label} changes.`}
              </p>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPendingCsv(null)}
                  className="bg-[#242424] hover:bg-[#2e2e2e] text-gray-300 text-sm px-4 py-2 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={commitImport}
                  disabled={pendingCsv.preview.error_count > 0 || busy === 'commit'}
                  className="bg-[#C41E3A] hover:bg-[#a51830] disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm px-4 py-2 rounded-lg transition-colors"
                >
                  {busy === 'commit' ? 'Applying…' : 'Apply'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {toast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 bg-[#1a1a1a] border border-[#2e2e2e] text-white text-sm px-4 py-2 rounded-lg shadow-lg z-50 flex items-center gap-2">
          <span>{toast}</span>
          <button onClick={() => setToast(null)} className="text-gray-500 hover:text-white">
            <X size={14} />
          </button>
        </div>
      )}
    </>
  )
}

function PreviewCount({ label, value, color }: { label: string; value: number; color: string }) {
  return (
    <div className="bg-[#242424] border border-[#2e2e2e] rounded-lg p-3">
      <p className="text-xs text-gray-500">{label}</p>
      <p className={`text-2xl font-bold tabular-nums ${color}`}>{value.toLocaleString()}</p>
    </div>
  )
}

function SampleList({ title, rows }: {
  title: string
  rows: Array<{ row: number; name: string | null; sku?: string | null; code?: string | null }>
}) {
  return (
    <div className="mb-3">
      <p className="text-xs uppercase tracking-wide text-gray-600 font-semibold mb-1.5">{title}</p>
      <ul className="text-xs text-gray-400 space-y-0.5">
        {rows.map(s => {
          const ident = s.sku ?? s.code ?? '(no key)'
          return <li key={s.row}>row {s.row}: {ident} — {s.name ?? '(no name)'}</li>
        })}
      </ul>
    </div>
  )
}
